/* =============================================================
   OnlyLewds — Custom Currency Converter (display-only)
   Base/checkout currency: CAD. This only rewrites DISPLAYED
   prices into the visitor's chosen currency. Replaces Beast.

   v3 (2026-06-23):
   - Converts at the TEXT-NODE level, so it also catches the custom
     section pattern  <span class="ol-pr">$199.99<small>CAD</small></span>
     (a parent with a text node + child) that leaf-only scanning missed.
   - Updates the adjacent ISO-code label (the <small>) to the active code.
   - Rounds converted prices UP to .99 (charm pricing) — base CAD untouched.
   - Self-healing: re-converts after the cart drawer / sections re-render
     in CAD (idempotent writes, so no loops).
   ============================================================= */
(function () {
  'use strict';

  // ---- Config -------------------------------------------------
  var BASE = 'CAD';
  var CURRENCIES = {
    CAD: { symbol: '$',  flag: '🇨🇦', label: 'CAD' },
    USD: { symbol: '$',  flag: '🇺🇸', label: 'USD' },
    EUR: { symbol: '€',  flag: '🇪🇺', label: 'EUR' },
    GBP: { symbol: '£',  flag: '🇬🇧', label: 'GBP' },
    AUD: { symbol: 'A$', flag: '🇦🇺', label: 'AUD' }
  };
  var SYMBOLS = Object.keys(CURRENCIES).filter(function (c) { return c !== BASE; });
  var RATE_API = 'https://api.frankfurter.dev/v1/latest?base=' + BASE +
                 '&symbols=' + SYMBOLS.join(',');
  var RATE_KEY = 'ol_fx_rates';
  var CUR_KEY  = 'ol_currency';
  var TTL_MS   = 24 * 60 * 60 * 1000; // 24h

  // A text node is a price if its WHOLE value is a money amount. Allows the
  // store's base "$" plus the prefixes other money formatters emit (CA$, US$,
  // A$, €, £) and an optional trailing ISO code.
  var MONEY_RE = /^\s*(?:CA|C|US|U|AU|A)?\s*[$€£]\s*\d[\d,]*(?:\.\d{1,2})?\s*(?:CAD|USD|EUR|GBP|AUD)?\s*$/i;
  // A leaf element whose entire text is just an ISO code (the <small> label).
  var ISO_RE = /^\s*(CAD|USD|EUR|GBP|AUD)\s*$/i;

  var rates = { CAD: 1 };
  var current = getStored(CUR_KEY) || BASE;
  var writing = false;

  var cadMap  = new WeakMap(); // textNode  -> base CAD amount
  var origMap = new WeakMap(); // textNode  -> original base string
  var isoMap  = new WeakMap(); // labelEl   -> original ISO code

  // ---- Helpers ------------------------------------------------
  function getStored(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function setStored(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function parseAmt(t) {
    var n = parseFloat(String(t).replace(/[^\d.]/g, ''));
    return isNaN(n) ? null : n;
  }
  function group(s) { return s.replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  // Smallest value ending in .99 that is >= v (round UP to charm price).
  function round99(v) { return Math.ceil(v - 0.99 - 1e-9) + 0.99; }

  function convertAmount(cad, cur, withCode) {
    var v = cad * (rates[cur] || 1);
    if (v > 0.01) v = round99(v);        // .99 charm rounding for converted prices
    var c = CURRENCIES[cur];
    return c.symbol + group(v.toFixed(2)) + (withCode ? ' ' + c.label : '');
  }

  // Find ISO-code label elements (e.g. <small>CAD</small>) that belong to a price.
  // Checks the text node's own parent (price as a direct child, e.g. JS-rendered
  // .ol-pr) AND its grandparent (price wrapped in .money, e.g. server .ol-pr).
  function isoLabelsFor(tn) {
    var labels = [];
    function scan(c, exclude) {
      if (!c || !c.children) return;
      for (var i = 0; i < c.children.length; i++) {
        var el = c.children[i];
        if (el === exclude) continue;
        if (el.children.length === 0 && ISO_RE.test(el.textContent || '')) labels.push(el);
      }
    }
    scan(tn.parentNode, null);
    scan(tn.parentNode && tn.parentNode.parentNode, tn.parentNode);
    return labels;
  }

  // Some places embed the price in combined text: promo pills ("Quickie · $9.99")
  // and the cart's Collector Reward / shipping bars ("$279.99 / CA$300"). Convert
  // price tokens IN PLACE, scoped to those containers only so we never touch
  // threshold copy ("Spend $300") elsewhere on the site.
  var PILL_SEL = '[class*="hero__pill"], .ol-thresh-amt';
  var TOKEN_RE = /(?:CA|US|A)?\$\s?\d[\d,]*(?:\.\d{1,2})?/g;
  var subOrig = new WeakMap(); // textNode -> original combined string
  function substringConvert(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var conts = [];
    if (scope.matches && scope.matches(PILL_SEL)) conts.push(scope);
    var found = scope.querySelectorAll(PILL_SEL);
    for (var a = 0; a < found.length; a++) conts.push(found[a]);
    for (var k = 0; k < conts.length; k++) {
      var cont = conts[k];
      if (cont.closest && cont.closest('#ol-currency')) continue;
      var cn = cont.childNodes;
      for (var j = 0; j < cn.length; j++) {
        var node = cn[j];
        if (node.nodeType !== 3) continue;
        var orig;
        if (subOrig.has(node)) orig = subOrig.get(node);
        else {
          if (!/\$\s?\d/.test(node.nodeValue)) continue;
          orig = node.nodeValue; subOrig.set(node, orig);
        }
        var out = (current === BASE) ? orig : orig.replace(TOKEN_RE, function (tok) {
          var cad = parseAmt(tok);
          return cad === null ? tok : convertAmount(cad, current, true);
        });
        if (node.nodeValue !== out) node.nodeValue = out;
      }
    }
  }

  // ---- Rates --------------------------------------------------
  function loadRates() {
    var cached = getStored(RATE_KEY);
    if (cached) {
      try {
        var o = JSON.parse(cached);
        if (o && o.t && (Date.now() - o.t) < TTL_MS && o.r) {
          rates = Object.assign({ CAD: 1 }, o.r);
          return Promise.resolve();
        }
      } catch (e) {}
    }
    return fetch(RATE_API)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.rates) {
          rates = Object.assign({ CAD: 1 }, data.rates);
          setStored(RATE_KEY, JSON.stringify({ t: Date.now(), r: data.rates }));
        }
      })
      .catch(function () { /* keep CAD-only fallback */ });
  }

  // ---- Price rewriting (text-node level) ---------------------
  function rewriteNode(tn) {
    var p = tn.parentNode;
    if (!p || p.nodeType !== 1) return;
    if (p.id === 'ol-currency' || p.closest('#ol-currency')) return;

    var base;
    if (cadMap.has(tn)) {
      base = cadMap.get(tn);
    } else {
      if (!MONEY_RE.test(tn.nodeValue)) return;
      base = parseAmt(tn.nodeValue);
      if (base === null) return;
      cadMap.set(tn, base);
      origMap.set(tn, tn.nodeValue);
    }

    var labels = isoLabelsFor(tn);
    var withCode = labels.length === 0; // if a separate ISO label exists, it carries the code
    var orig = origMap.get(tn);
    var out;
    if (current === BASE) {
      out = orig;
    } else {
      var lead = (orig.match(/^\s*/) || [''])[0];
      var trail = (orig.match(/\s*$/) || [''])[0];
      out = lead + convertAmount(base, current, withCode) + trail; // keep original spacing
    }
    if (out != null && tn.nodeValue !== out) tn.nodeValue = out;

    for (var i = 0; i < labels.length; i++) {
      var el = labels[i];
      if (!isoMap.has(el)) isoMap.set(el, el.textContent);
      var iso = (current === BASE) ? isoMap.get(el) : CURRENCIES[current].label;
      if (el.textContent !== iso) el.textContent = iso;
    }
  }

  function rewrite(root) {
    root = root || document.body;
    if (root.nodeType === 3) { rewriteNode(root); return; }
    writing = true;
    try {
      var tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: function (n) {
          var p = n.parentNode;
          if (!p || p.nodeType !== 1) return NodeFilter.FILTER_REJECT;
          if (p.id === 'ol-currency' || p.closest('#ol-currency, script, style')) return NodeFilter.FILTER_REJECT;
          if (cadMap.has(n)) return NodeFilter.FILTER_ACCEPT;
          return MONEY_RE.test(n.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        }
      });
      var nodes = [], n;
      while ((n = tw.nextNode())) nodes.push(n);
      for (var i = 0; i < nodes.length; i++) rewriteNode(nodes[i]);
      substringConvert(root); // promo pills with prices embedded in combined text
    } finally {
      writing = false;
    }
    updateNote();
  }

  // CAD disclaimer note (cart drawer). Shown only for non-CAD.
  function updateNote() {
    var note = document.getElementById('ol-currency-note');
    if (!note) return;
    var label = document.getElementById('ol-currency-label');
    if (current === BASE) { note.style.display = 'none'; }
    else { if (label) label.textContent = CURRENCIES[current].label; note.style.display = ''; }
  }

  // ---- Self-healing observer (efficient: only touched subtrees) ----
  function observe() {
    var queue = [], scheduled = false;
    function flush() {
      scheduled = false;
      var roots = queue.splice(0, queue.length);
      if (!roots.length) return;
      for (var i = 0; i < roots.length; i++) {
        var r = roots[i];
        if (r && (r.nodeType === 3 || r.isConnected)) rewrite(r);
      }
    }
    var mo = new MutationObserver(function (muts) {
      if (writing) return;
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i];
        if (m.type === 'childList') {
          for (var j = 0; j < m.addedNodes.length; j++) {
            var nd = m.addedNodes[j];
            if (nd.nodeType === 1) queue.push(nd);
            else if (nd.nodeType === 3 && nd.parentNode) queue.push(nd.parentNode);
          }
        } else if (m.type === 'characterData' && m.target && m.target.parentNode) {
          queue.push(m.target.parentNode);
        }
      }
      if (queue.length && !scheduled) { scheduled = true; requestAnimationFrame(flush); }
    });
    mo.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  // ---- Selector pill UI --------------------------------------
  function buildUI() {
    if (document.getElementById('ol-currency')) return;
    var wrap = document.createElement('div');
    wrap.id = 'ol-currency';
    wrap.setAttribute('aria-label', 'Currency selector');

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ol-cur__btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');

    var menu = document.createElement('ul');
    menu.className = 'ol-cur__menu';
    menu.setAttribute('role', 'listbox');

    Object.keys(CURRENCIES).forEach(function (code) {
      var c = CURRENCIES[code];
      var li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.className = 'ol-cur__opt';
      li.dataset.code = code;
      li.innerHTML = '<span class="ol-cur__flag">' + c.flag + '</span>' + c.label;
      li.addEventListener('click', function () { select(code); });
      menu.appendChild(li);
    });

    wrap.appendChild(menu);
    wrap.appendChild(btn);
    document.body.appendChild(wrap);
    if (!getStored(CUR_KEY)) wrap.classList.add('ol-cur--pulse'); // first-visit attention hint

    function renderBtn() {
      var c = CURRENCIES[current];
      btn.innerHTML = '<span class="ol-cur__flag">' + c.flag + '</span>' +
                      c.label + '<span class="ol-cur__caret">▾</span>';
      menu.querySelectorAll('.ol-cur__opt').forEach(function (o) {
        o.setAttribute('aria-selected', o.dataset.code === current ? 'true' : 'false');
      });
    }
    function open(state) {
      wrap.classList.toggle('is-open', state);
      btn.setAttribute('aria-expanded', state ? 'true' : 'false');
    }
    btn.addEventListener('click', function (e) { e.stopPropagation(); open(!wrap.classList.contains('is-open')); });
    document.addEventListener('click', function () { open(false); });

    window.__olSelect = function (code) { select(code); };
    function select(code) {
      if (!CURRENCIES[code]) return;
      current = code;
      setStored(CUR_KEY, code);
      renderBtn();
      open(false);
      rewrite(document.body);
    }
    renderBtn();
  }

  // ---- Init ---------------------------------------------------
  function init() {
    buildUI();
    rewrite(document.body);
    loadRates().then(function () {
      rewrite(document.body);
      observe();
      [250, 800, 2000, 4000].forEach(function (ms) { setTimeout(function () { rewrite(document.body); }, ms); });
    });
    window.addEventListener('pageshow', function () { rewrite(document.body); });
    window.addEventListener('load', function () { rewrite(document.body); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
